import { useCallback, useEffect, useState } from 'react';
import { stateSetter } from '@/utils';
import { fetchQuerySubscriptions, fetchSubscriptions } from './actions';
import { ISubscribedProject, UserSubscriptionsReturnType } from './types';

// Fallback stabil ca identitate pentru `data` neîncărcat — un `[]` nou la fiecare
// randare re-declanșează în lanț efectele consumatorilor cu dependență pe `data`
// („Maximum update depth exceeded").
const EMPTY_LIST: never[] = [];

export function useUserSubscriptions<
	T extends ISubscribedProject | ISubscribedProject[]
>(
	userId: number | string | string | undefined = undefined,
	queryParam: string | undefined = undefined,
	pageSize = 10
): UserSubscriptionsReturnType<T> {
	const [data, setData] = useState<T | null>(null);
	const [loading, setLoading] = useState<boolean>(true);
	const [enableQuery, setEnableQuery] = useState<boolean>(false);
	const [page, setPage] = useState<number>(0);
	const [items, setItems] = useState<number>(pageSize);
	const [totalPages, setTotalPages] = useState<number>(1);
	const [totalItems, setTotalItems] = useState<number>(1);

	const handleResponse = (pageParam: number, itemsParam: number): void => {
		setTotalPages(pageParam);
		setTotalItems(itemsParam);
	};

	const getSubscriptions = useCallback(
		async (id: number | string, pageParam: number, itemsParam: number) => {
			try {
				setLoading(true);
				const response = await fetchSubscriptions(
					id,
					pageParam,
					itemsParam,
					handleResponse
				);
				setData(response as T);
			} catch {
				// eroarea ajunge în toast + jurnal prin pipeline-ul global de erori
			} finally {
				setLoading(false);
			}
		},
		 
		[setData]
	);

	const getQuerySubscriptions = useCallback(
		async (id: number | string, query: string, queryPage: number, queryItems: number) => {
			try {
				!enableQuery && setEnableQuery(true);
				setLoading(true);
				const response = await fetchQuerySubscriptions(
					id,
					query,
					queryPage,
					queryItems,
					handleResponse
				);
				setData(response as T);
			} catch {
				// eroarea ajunge în toast + jurnal prin pipeline-ul global de erori
			} finally {
				setLoading(false);
			}
		},
		 
		[setData]
	);

	useEffect(() => {
		if (userId && queryParam === undefined) {
			getSubscriptions(userId, page, items);
		}
		 
	}, [userId, queryParam, page, items]);

	useEffect(() => {
		if (queryParam && userId) {
			getQuerySubscriptions(userId, queryParam, page, items);
		} else if (enableQuery && userId) {
			getSubscriptions(userId, page, items);
		}
		 
	}, [queryParam, userId, page, items]);

	return {
		data: data || (EMPTY_LIST as ISubscribedProject[] as T),
		setData: setData as stateSetter<T>,
		getSubscriptions,
		loading,
		items,
		page,
		setItems,
		setPage,
		totalPages,
		totalItems
	};
}
